import { useEffect, useState } from "react";

import { safeGet, safeSet } from "../../lib/safeStorage";

export const CONSENT_STORAGE_KEY = "voltaroute.consent";
export const CONSENT_PROMPT_KEY = "voltaroute.consent.prompted";

export type ConsentCategory = "analytics" | "crashReports";

export type Consent = {
  analytics: boolean;
  crashReports: boolean;
};

const DEFAULT_CONSENT: Consent = {
  analytics: false,
  crashReports: false,
};

const subscribers = new Set<(c: Consent) => void>();
let cached: Consent | null = null;

export function getConsent(): Consent {
  if (cached) return cached;
  const raw = safeGet(CONSENT_STORAGE_KEY);
  if (!raw) {
    cached = DEFAULT_CONSENT;
    return cached;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<Consent>;
    cached = {
      analytics: parsed.analytics === true,
      crashReports: parsed.crashReports === true,
    };
  } catch (err) {
    // GDPR-relevant: a corrupted blob silently resetting to defaults could
    // re-enable tracking the user opted out of. Log so it surfaces in any
    // user-shared console export and doesn't fail silently.
    console.warn("[consent] localStorage value was unparseable; resetting to defaults", err);
    cached = DEFAULT_CONSENT;
  }
  return cached;
}

export function setConsent(category: ConsentCategory, value: boolean): void {
  const current = getConsent();
  if (current[category] === value) return;
  const next = { ...current, [category]: value };
  cached = next;
  safeSet(CONSENT_STORAGE_KEY, JSON.stringify(next));
  for (const fn of subscribers) fn(next);
}

export function setAllConsent(next: Consent): void {
  const current = getConsent();
  if (current.analytics === next.analytics && current.crashReports === next.crashReports) return;
  cached = next;
  safeSet(CONSENT_STORAGE_KEY, JSON.stringify(next));
  for (const fn of subscribers) fn(next);
}

export function hasBeenPrompted(): boolean {
  return safeGet(CONSENT_PROMPT_KEY) === "1";
}

export function markPrompted(): void {
  safeSet(CONSENT_PROMPT_KEY, "1");
}

export function useConsent(): Consent {
  const [state, setState] = useState<Consent>(getConsent);
  useEffect(() => {
    subscribers.add(setState);
    return () => {
      subscribers.delete(setState);
    };
  }, []);
  return state;
}

// Test-only reset; harmless in production but never imported there.
export function __resetConsentForTests(): void {
  cached = null;
  subscribers.clear();
}

// Test-only direct subscribe — exposes the same wire as useConsent so tests
// can assert no-op guard semantics (subscriber NOT called when value
// didn't change). Production consumers should use useConsent().
export function __subscribeForTests(fn: (c: Consent) => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}
